import { useNavigate } from 'react-router-dom';
import useInView from '../../../hooks/useInView';

const badges = [
  { icon: 'ri-shield-check-fill', text: 'Licensed & Insured' },
  { icon: 'ri-star-fill', text: '4.9 Google Rating' },
  { icon: 'ri-customer-service-2-fill', text: '24/7 Service' },
  { icon: 'ri-bank-card-fill', text: 'Financing Available' },
  { icon: 'ri-award-fill', text: 'NATE Certified' },
];

const stats = [
  { value: '50K+', label: 'Homes Served' },
  { value: '4.9★', label: 'Google Rating' },
  { value: 'Florida', label: 'Statewide Service' },
];

const HeroSection = () => {
  const navigate = useNavigate();
  const { ref, inView } = useInView({ threshold: 0.05 });

  return (
    <section className="bg-white nav-offset overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center py-10 lg:py-20">

          {/* Left — Text Content */}
          <div className={`transition-all duration-1000 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-5 leading-tight tracking-tight">
              <span style={{ color: '#1E3A8A' }}>Total Home</span>
              <span className="block">
                <span style={{ color: '#C41E3A' }}>Comfort</span>
                {' '}
                <span style={{ color: '#1E3A8A' }}>Solutions</span>
              </span>
            </h1>

            <p className="text-base sm:text-lg text-[#6B7280] mb-3 leading-relaxed max-w-lg">
              HVAC, Plumbing, Electrical &amp; Energy Services for modern homeowners.
            </p>
            <p className="text-sm text-[#9CA3AF] mb-7 sm:mb-10 max-w-md leading-relaxed">
              Expert installation, service, and maintenance for residential and light commercial clients, backed by financing, protection plans, and industry-leading warranties.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-7 sm:mb-10">
              <button
                onClick={() => navigate('/book-appointment')}
                className="bg-[#C41E3A] text-white px-6 sm:px-7 py-3.5 sm:py-4 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-all whitespace-nowrap cursor-pointer"
              >
                Book Appointment
              </button>
              <button
                onClick={() => navigate('/book-appointment')}
                className="border-2 border-[#1A56A0] text-[#1A56A0] px-6 sm:px-7 py-3.5 sm:py-4 rounded-lg text-sm font-semibold hover:bg-[#1A56A0] hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                Get Free Estimate
              </button>
              <button
                onClick={() => navigate('/financing')}
                className="flex items-center justify-center space-x-2 text-[#0C2340] px-5 py-3.5 sm:py-4 rounded-lg text-sm font-semibold hover:bg-[#EEF5FF] transition-all whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bank-card-line text-[#C41E3A]" />
                </div>
                <span>View Financing</span>
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {badges.map((b, i) => (
                <div
                  key={b.text}
                  style={{ transitionDelay: `${200 + i * 80}ms` }}
                  className={`flex items-center space-x-1.5 bg-[#EEF5FF] border border-[#1A56A0]/20 rounded-lg px-3 py-2 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${b.icon} text-sm text-[#1A56A0]`} />
                  </div>
                  <span className="text-xs text-[#374151] font-medium whitespace-nowrap">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Image Panel */}
          <div
            style={{ transitionDelay: '300ms' }}
            className={`relative hidden lg:block transition-all duration-1000 ease-out ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
          >
            <div className="relative rounded-2xl overflow-hidden h-[480px] w-full">
              <img
                alt="Renova professional technician with homeowner home comfort services"
                title="Renova – Total Home Comfort Solutions"
                className="w-full h-full object-cover object-top"
                src="https://public.readdy.ai/ai/img_res/edited_6e0085860643365f0dcaead15102fea5_5646f5c8.jpg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C2340]/30 via-transparent to-transparent" />

              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 border border-[#E2E8F0]">
                <div className="grid grid-cols-3 divide-x divide-[#E2E8F0]">
                  {stats.map((s) => (
                    <div key={s.label} className="text-center px-3">
                      <p className="text-xl font-bold text-[#0C2340]">{s.value}</p>
                      <p className="text-xs text-[#6B7280] mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="absolute -left-8 top-1/2 -translate-y-1/2 bg-white rounded-xl p-4 border border-[#E2E8F0] w-52">
              <div className="space-y-3">
                {[
                  { icon: 'ri-calendar-check-line', label: 'Book Online', sub: 'Schedule in 60 sec', path: '/book-appointment' },
                  { icon: 'ri-money-dollar-circle-line', label: 'Free Estimate', sub: 'No obligation', path: '/book-appointment' },
                  { icon: 'ri-bank-card-line', label: 'Apply Financing', sub: '0% APR available', path: '/financing' },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => navigate(item.path)}
                    className="w-full flex items-center space-x-2 hover:bg-[#F8F9FB] rounded-lg p-2 transition-all cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 bg-[#C41E3A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-white text-sm`} />
                    </div>
                    <div>
                      <div className="text-[#111827] text-xs font-semibold whitespace-nowrap">{item.label}</div>
                      <div className="text-[#9CA3AF] text-xs whitespace-nowrap">{item.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-[#E2E8F0] text-center">
                <p className="text-[#9CA3AF] text-xs mb-1">24/7 Emergency</p>
                <a href="tel:+18506698335" className="text-[#0C2340] font-bold text-sm hover:text-[#C41E3A] transition-colors">
                  (850) 669-8335
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-[#E2E8F0]" />
    </section>
  );
};

export default HeroSection;
